import React, { useState, useEffect } from 'react';
import { changeMyPassword, getMe } from '../../services/api';
import { validatePassword, PASSWORD_POLICY_MESSAGE } from '../../utils/passwordUtils';
import { getFieldError, handleError } from '../../utils/errorUtils';
import { showSuccessToastFullScreen } from '../../utils/toastUtils';
import { captureEvent } from '../../services/analytics';
import ModalContainer from '../modals/ModalContainer';
import PasswordField from '../inputs/PasswordField/PasswordField';
import css from './ChangePasswordSection.module.scss';

const ChangePasswordSection: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [currentError, setCurrentError] = useState<string>();
  const [nextError, setNextError] = useState<string>();
  const [confirmError, setConfirmError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  // The account email, surfaced only as a hidden username field so password managers
  // associate the updated credential. Fetched lazily when the dialog opens.
  const [username, setUsername] = useState('');

  useEffect(() => {
    if (isOpen && !username) {
      getMe().then((me) => setUsername(me.username)).catch(() => {});
    }
  }, [isOpen, username]);

  const resetForm = () => {
    setCurrent(''); setNext(''); setConfirm('');
    setCurrentError(undefined); setNextError(undefined); setConfirmError(undefined);
  };

  const close = () => {
    if (submitting) return; // don't yank the dialog out from under an in-flight request
    setIsOpen(false);
    resetForm();
  };

  // Esc closes the dialog, mirroring the delete-account modal.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, submitting]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentError(undefined); setNextError(undefined); setConfirmError(undefined);

    const check = validatePassword(next);
    if (!check.ok) { setNextError(check.error); return; }
    if (next !== confirm) { setConfirmError('Passwords do not match.'); return; }

    setSubmitting(true);
    try {
      await changeMyPassword(current, next);
      captureEvent('password_changed', {});
      showSuccessToastFullScreen('Password changed.');
      setIsOpen(false);
      resetForm();
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401) setCurrentError('Current password is incorrect.');
      else if (status === 422) setNextError(getFieldError(error, 'newPassword') ?? PASSWORD_POLICY_MESSAGE);
      else handleError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={css.section}>
      <h2>Password</h2>
      <div className={css.row}>
        <p className={css.description}>Change the password you use to sign in.</p>
        <button type="button" className={css.openButton} onClick={() => setIsOpen(true)}>
          Change password
        </button>
      </div>

      <ModalContainer isOpen={isOpen} onClose={close}>
        <div className={css.modal} role="dialog" aria-modal="true" aria-label="Change password">
          <h2 className={css.modalTitle}>Change password</h2>
          <form className={css.form} onSubmit={handleSubmit}>
            {/* Hidden identity field so password managers update the right credential. */}
            <input type="text" name="username" autoComplete="username" value={username} readOnly hidden />

            <label htmlFor="currentPassword">Current password</label>
            <PasswordField id="currentPassword" name="currentPassword" value={current}
              autoComplete="current-password" error={currentError}
              onChange={(v) => { setCurrent(v); setCurrentError(undefined); }} />

            <label htmlFor="newPassword">New password</label>
            <PasswordField id="newPassword" name="newPassword" value={next} showChecklist
              autoComplete="new-password" error={nextError}
              onChange={(v) => { setNext(v); setNextError(undefined); setConfirmError(undefined); }} />

            <label htmlFor="confirmPassword">Confirm new password</label>
            <PasswordField id="confirmPassword" name="confirmPassword" value={confirm}
              autoComplete="new-password" error={confirmError}
              onChange={(v) => { setConfirm(v); setConfirmError(undefined); }} />

            <div className={css.actions}>
              <button type="button" className={css.cancel} onClick={close} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className={css.submit} disabled={submitting}>
                {submitting ? 'Updating…' : 'Update password'}
              </button>
            </div>
          </form>
        </div>
      </ModalContainer>
    </section>
  );
};

export default ChangePasswordSection;
